/**
 * Shared helpers for building rule-condition field dropdowns (Publish Rules and
 * Vehicle Groupings).
 *
 * The available rule-condition fields come from the server-owned catalog
 * (GET /mapping-config/rule-fields), NOT from an account's already-saved rules.
 * That keeps availability intentional and identical across accounts.
 *
 * Fields that appear only inside an account's saved rules but are NOT in the
 * catalog are handled as display-only "legacy" entries: they are appended so
 * existing selections don't render blank, but they are clearly labeled and are
 * never promoted to first-class, selectable available fields.
 */

export interface RuleConditionFieldOption {
  title: string
  value: string
  /** True when provided by the system rather than configured in Field Mapping. */
  systemProvided?: boolean
  /** True for display-only entries kept so saved rules don't render blank. */
  legacy?: boolean
}

/**
 * Static fallback used only when the rule-fields API is unreachable. Mirrors the
 * always-present default rule fields so the editor remains usable offline. This
 * is a degraded fallback, not a source of truth.
 */
export const DEFAULT_RULE_CONDITION_FIELD_OPTIONS: RuleConditionFieldOption[] = [
  { title: 'Make', value: 'make' },
  { title: 'Model', value: 'model' },
  { title: 'VIN', value: 'vin' },
  { title: 'Year', value: 'year' },
]

/**
 * Append display-only legacy entries for fields that are referenced by saved
 * rules but are not part of the catalog, so the corresponding selects render
 * their value instead of going blank.
 */
export function withLegacyUsedFields(
  catalog: RuleConditionFieldOption[],
  usedFields: Iterable<string>,
): RuleConditionFieldOption[] {
  const known = new Set(catalog.map((o) => o.value))
  const legacy: RuleConditionFieldOption[] = []
  const seen = new Set<string>()
  for (const raw of usedFields) {
    const field = String(raw || '').trim()
    if (!field || known.has(field) || seen.has(field)) continue
    seen.add(field)
    legacy.push({ title: `${field} (legacy)`, value: field, legacy: true })
  }
  return [...catalog, ...legacy.sort((a, b) => a.title.localeCompare(b.title))]
}
